/**
 * Aviso de mensagem nova: floater no topo, centralizado, + som. Fica montado enquanto a tela de
 * atendimento está aberta e vigia a lista de conversas (polling curto de `useInboxPulse`).
 * Clicar no aviso abre a conversa; o botão de sino liga/desliga só o SOM (o aviso visual fica).
 */
import React, { useEffect, useRef, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { Bell, BellOff } from "lucide-react";
import type { PlatformConversation } from "../../api/types.js";
import { ChatAvatar } from "./ChatAvatar.js";
import { detectNewInbound, playMessageSound, readSoundEnabled, snapshotOf, writeSoundEnabled, type InboxSnapshot, type NewInboundAlert } from "./newMessageAlerts.js";
import { stripJid } from "./helpers.js";

const TOAST_MS = 7_000;
const MAX_TOASTS = 3;

type Toast = NewInboundAlert & { key: number; count: number };

export function InboxAlerts({ conversations, openId, pictureFor }: { conversations: PlatformConversation[] | undefined; openId: string | null; pictureFor: (conversation: PlatformConversation) => string | null }): ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [sound, setSound] = useState(readSoundEnabled);
  const snapshot = useRef<InboxSnapshot | null>(null);
  const sequence = useRef(0);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  useEffect(() => {
    if (!conversations) return;
    // Aba em segundo plano: a conversa aberta também avisa (o operador não a está vendo).
    const visible = typeof document === "undefined" || document.visibilityState === "visible";
    const alerts = detectNewInbound(snapshot.current, conversations, {
      isOpen: (id) => visible && id === openId,
      describe: (conversation) => ({ name: stripJid(conversation.displayName || conversation.customerId), pictureUrl: pictureFor(conversation) })
    });
    snapshot.current = snapshotOf(conversations);
    if (!alerts.length) return;
    if (soundRef.current) playMessageSound();
    setToasts((current) => {
      let next = [...current];
      for (const alert of alerts) {
        const existing = next.find((toast) => toast.conversationId === alert.conversationId);
        // Mesma conversa de novo: atualiza o texto e conta, em vez de empilhar avisos repetidos.
        next = existing
          ? next.map((toast) => (toast === existing ? { ...alert, key: ++sequence.current, count: toast.count + 1 } : toast))
          : [...next, { ...alert, key: ++sequence.current, count: 1 }];
      }
      return next.slice(-MAX_TOASTS);
    });
  }, [conversations]);

  // Cada aviso some sozinho; a chave nova (mensagem repetida) reinicia o relógio.
  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map((toast) => setTimeout(() => setToasts((current) => current.filter((item) => item.key !== toast.key)), TOAST_MS));
    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  return (
    <>
      <button
        type="button"
        onClick={() => { const next = !sound; setSound(next); writeSoundEnabled(next); if (next) playMessageSound(); }}
        aria-pressed={sound}
        aria-label={sound ? "Desligar som de mensagem nova" : "Ligar som de mensagem nova"}
        title={sound ? "Som de mensagem nova ligado" : "Som de mensagem nova desligado"}
        data-action="toggle-alert-sound"
        className="inline-flex h-8 w-8 items-center justify-center rounded-pill text-muted transition-colors hover:bg-surface-soft hover:text-ink"
      >
        {sound ? <Bell className="h-4 w-4" aria-hidden focusable={false} /> : <BellOff className="h-4 w-4" aria-hidden focusable={false} />}
      </button>
      <div className="pointer-events-none fixed left-1/2 top-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2" role="status" aria-live="polite" data-inbox-toasts={toasts.length}>
        {toasts.map((toast) => (
          <Link
            key={toast.key}
            to={`/inbox/${toast.conversationId}`}
            onClick={() => setToasts((current) => current.filter((item) => item.key !== toast.key))}
            className="pointer-events-auto flex items-center gap-3 rounded-panel border border-border bg-surface px-4 py-3 shadow-s2 transition-transform hover:-translate-y-0.5"
            data-inbox-toast={toast.conversationId}
          >
            <ChatAvatar name={toast.name} pictureUrl={toast.pictureUrl} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-extrabold text-ink">{toast.name}</span>
              <span className="truncate text-sm text-muted">{toast.preview}</span>
            </span>
            {toast.count > 1 ? <span className="rounded-pill bg-brand px-2 text-xs font-extrabold text-on-brand">{toast.count}</span> : <span className="text-xs font-bold text-brand-text">Nova</span>}
          </Link>
        ))}
      </div>
    </>
  );
}
